import { useCallback, useEffect } from 'react';

import { type Table } from '@tanstack/react-table';
import { bind } from 'bind-event-listener';

import { token } from '@atlaskit/tokens';

import {
	cssPinningBackgroundLeft,
	cssPinningBackgroundRight,
} from '../../wrappers/charlie-table-dnd-wrapper';

type UseTablePinningEffectsArgs<T extends object> = {
	tableRef: React.RefObject<HTMLTableElement>;
	wrapperRef: React.RefObject<HTMLDivElement>;
	tbodyRef: React.RefObject<HTMLTableSectionElement>;
	charlieTableState: Table<T>;
};

/**
 * Sets up scroll and resize observers to manage pinning background gradients.
 */
export const useTablePinningEffects = <T extends object>({
	tableRef,
	wrapperRef,
	tbodyRef,
	charlieTableState,
}: UseTablePinningEffectsArgs<T>) => {
	const onScroll = useCallback(() => {
		const table = tableRef.current;
		const wrapper = wrapperRef.current;
		if (!table || !wrapper) {
			return;
		}

		const hasScrolled = wrapper && wrapper.scrollWidth - wrapper.scrollLeft !== wrapper.scrollWidth;
		const isMostRight = Math.ceil(wrapper.scrollLeft + wrapper.clientWidth) >= wrapper.scrollWidth;
		if (hasScrolled) {
			table.style.setProperty(
				cssPinningBackgroundLeft,
				`linear-gradient(90deg, ${token('color.border')} 0%, rgba(0,0,0,0) 100%)`,
			);
			table.style.setProperty(
				cssPinningBackgroundRight,
				isMostRight
					? 'none'
					: `linear-gradient(270deg, ${token('color.border')} 0%, rgba(0,0,0,0) 100%)`,
			);
		} else {
			table.style.setProperty(cssPinningBackgroundLeft, 'none');
		}
	}, [tableRef, wrapperRef]);

	// Set up ResizeObserver for right pinning background when table width changes
	useEffect(() => {
		const table = tableRef.current;
		const wrapper = wrapperRef.current;
		const tbody = tbodyRef.current;

		if (!table || !wrapper || !tbody) {
			return;
		}

		const resizeObserver = new ResizeObserver(() => {
			const hasScrollBar = wrapper.scrollWidth > wrapper.clientWidth;
			if (hasScrollBar) {
				table.style.setProperty(
					cssPinningBackgroundRight,
					`linear-gradient(270deg, ${token('color.border')} 0%, rgba(0,0,0,0) 100%)`,
				);
			} else {
				table.style.setProperty(cssPinningBackgroundRight, 'none');
			}
		});

		// Observe the tbody element since column resizing changes the tbody's width
		resizeObserver.observe(tbody);

		return () => {
			resizeObserver.disconnect();
		};
	}, [tableRef, wrapperRef, tbodyRef]);

	// Set up scroll listener for pinning styles
	useEffect(() => {
		const table = tableRef.current;
		const wrapper = wrapperRef.current;
		if (!charlieTableState.getIsSomeColumnsPinned() || !table || !wrapper) {
			return;
		}

		const unbind = wrapper
			? bind(wrapper, {
					type: 'scroll',
					listener: onScroll,
				})
			: undefined;
		return () => {
			unbind?.();
		};
	}, [charlieTableState, onScroll, tableRef, wrapperRef]);
};
